import { ResourceManager } from "../../../components/resource-manager/resource-manager.component";
import { DrawingLibrary } from "../../../components/drawing.library";
import { environment } from "../../../../environments/environment";
import { InspirationRoomOptionStyle } from "../../../models/InspirationRoomOptionModel";


interface ScreenBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export class Point {

  absoluteX: number;
  absoluteY: number;
  scale: number;

  get relativeX(): number {
    return Math.floor((this.absoluteX * this.scale));
  }

  get relativeY(): number {
    return Math.floor((this.absoluteY * this.scale));
  }
}

export interface OptionCoordinates {
  id: string;
  x: number;
  y: number;
  iconPosition: IconPosition;
}

export enum SubLabelPosition {
  bottom,
  top
}

export enum IconPosition {
  left,
  right
}

export enum TipPosition {
  topLeft = 1,
  topRight = 2,
  bottomLeft = 3,
  bottomRight = 4
}

export class OptionTag {

  anchor: Point;
  bounds: ScreenBounds;
  adminFlipBounds: ScreenBounds;
  absoluteX: number;
  absoluteY: number;
  id: number;
  optionId: number;
  label: string;
  subLabel: string;
  price: number;
  isActive: boolean;
  inActive: boolean;
  selected: boolean;
  subLabelPosition: SubLabelPosition = SubLabelPosition.bottom;
  iconPosition: IconPosition = IconPosition.left;
  tipPosition: TipPosition = TipPosition.topLeft;
  selectedGlobalOption: number;
  isBoundsSet = false;
  isCollapsed: boolean;
  isNewOption: boolean;
  isDeleted: boolean;

  private selectedIcon: HTMLImageElement;
  private newOptionIcon: HTMLImageElement;
  private unselectedIcon: HTMLImageElement;
  private infoIcon: HTMLImageElement;
  private SCImage: HTMLImageElement;
  private swapIcon: HTMLImageElement;

  isSelected: boolean;
  selectedOffsetX: number;
  selectedOffsetY: number;

  // Label Widths & Formatting
  formattedPrice: string;

  calculatedWidth = 0;
  labelWidth = 0;
  subLabelWidth = 0;
  priceWidth = 0;
  animatingClick: boolean;
  animationIconSize: number;
  animationOpacity: number;
  iconSize = 24;

  // Temp fade animation
  collapsedOpacity: number;
  isFading = false;

  optionCoordinates: OptionCoordinates[] = [];

  selectedRoomId: string;
  isDesignerMode: boolean;
  isDesignerTip: boolean;
  drawAlpha = 1;
  styleSelections: InspirationRoomOptionStyle[]

  constructor(private ctx: CanvasRenderingContext2D, private resourceManager: ResourceManager) {
    this.SCImage = this.resourceManager.getImage("SC-Image");
    this.selectedIcon = this.resourceManager.getImage("favorite-icon");
    this.unselectedIcon = this.resourceManager.getImage("unfavorite-icon");
    this.infoIcon = this.resourceManager.getImage("info-icon");
    this.swapIcon = this.resourceManager.getImage("exchange-icon");
    this.newOptionIcon = this.resourceManager.getImage("newOption-icon");
    this.newOptionIcon = this.resourceManager.getImage("newOption-icon");
    this.collapsedOpacity = Math.random();
  }

  isCoordinateInBounds(x: number, y: number) {

    if (this.bounds
      && x >= this.bounds.x
      && y >= this.bounds.y
      && x <= this.bounds.x + this.bounds.width
      && y <= this.bounds.y + this.bounds.height) {
      return true;
    }
    return false;
  }

  getCoordsForRoom(roomKey: string): Point {
    for (const coord of this.optionCoordinates) {
      if (coord.id === roomKey) {
        const point = new Point();
        point.absoluteX = coord.x;
        point.absoluteY = coord.y;
        return point;
      }
    }
    return null;
  }

  drawDesignerControls() {
    if (this.isSelected && !this.isCollapsed && !this.isNewOption) {

      this.ctx.fillStyle = "#000";
      this.ctx.globalAlpha = .9;

      // Background Circle
      this.ctx.fillStyle = environment.theme.primaryColor;
      this.ctx.strokeStyle = "#fff";
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.arc(this.anchor.relativeX + this.bounds.width - 29, this.anchor.relativeY - 34, 20, 0, 2 * Math.PI);
      this.ctx.fill();
      this.ctx.stroke();
      this.ctx.closePath();

      this.ctx.drawImage(this.swapIcon, this.anchor.relativeX + this.bounds.width - 41, this.anchor.relativeY - 46, 24, 24);

    }
  }
  drawNewOptionIcon() {
    if (this.isNewOption) {

      this.isCollapsed = false;
      this.ctx.font = "38px 'Poppins', sans-serif";
      this.ctx.fillStyle = '#ffffffbb';
      this.ctx.globalAlpha = 1;
      //this.ctx.fillStyle = environment.theme.primaryColor;
      //this.ctx.globalAlpha = 2;

      // Background Circle
      this.ctx.fillStyle = environment.theme.primaryColor;
      this.ctx.strokeStyle = "#fff";
      this.ctx.lineWidth = 4;
      this.ctx.beginPath();
      this.ctx.arc(this.anchor.relativeX + this.bounds.width - 29, this.anchor.relativeY - 34, 20, 0, 2 * Math.PI);
      this.ctx.fill();
      this.ctx.stroke();
      this.ctx.closePath();

      this.ctx.drawImage(this.newOptionIcon, this.anchor.relativeX + this.bounds.width - 41, this.anchor.relativeY - 46, 24, 24);

    }
  }

  draw(ctx: CanvasRenderingContext2D, scale: number, isDesignerMode: boolean) {
    this.anchor.scale = scale;

    this.isDesignerMode = isDesignerMode;
    if (this.isDesignerTip) {
      ctx.globalAlpha = 1;
    } else {
      ctx.globalAlpha = this.drawAlpha;
    }

    this.drawBackground(ctx);

    // Harshit(hide the icon to like unlike from inspiration screen)
    if (!isDesignerMode && !this.isDesignerTip) {
      this.drawIcon(ctx);
    }

    if (this.isDesignerTip) {
      this.drawInfoIcon(ctx);
    }
    if (isDesignerMode && !this.isDesignerTip) {
      this.drawDesignerControls();
      this.drawNewOptionIcon();
    }
  }

  fadeStep = .02;
  fadeIn(alphaStop: number) {
    if (this.drawAlpha < alphaStop) {
      this.drawAlpha = this.drawAlpha + this.fadeStep < alphaStop ? this.drawAlpha + this.fadeStep : alphaStop;
    }
  }

  fadeOut(alphaStop: number) {
    if (this.drawAlpha > alphaStop) {
      this.drawAlpha = this.drawAlpha - this.fadeStep > alphaStop ? this.drawAlpha - this.fadeStep : alphaStop;
    }
  }

  updateAnchor(x: number, y: number, scale: number, ctx: CanvasRenderingContext2D) {
    if (!this.anchor) {
      this.anchor = new Point();
    }

    this.anchor.absoluteX = x;
    this.anchor.absoluteY = y;
    this.anchor.scale = scale;
    this.isBoundsSet = false;
    this.getBounds(ctx);
  }

  //applyCoordinateUpdate(x: number, y: number, roomId: string) {
  //  for (const coord of this.optionCoordinates) {
  //    if (coord.id === roomId) {
  //      coord.x = x;
  //      coord.y = y;
  //      return;
  //    }
  //  }
  //}

  getBounds(ctx: CanvasRenderingContext2D) {

    if (!this.isBoundsSet) {

      // Initalize bounds if new.
      if (!this.bounds) {
        this.bounds = { x: 0, y: 0, width: 0, height: 0 };
        this.adminFlipBounds = { x: 0, y: 0, width: 0, height: 0 };
      }

      // Admin Flip Bounds
      this.adminFlipBounds.x = this.anchor.relativeX + this.bounds.width - 52;
      this.adminFlipBounds.y = this.anchor.relativeY - 56;
      this.adminFlipBounds.width = 45;
      this.adminFlipBounds.height = 45;
      this.bounds.x = this.anchor.relativeX;

      // Sub Label
      if (this.subLabel
        && this.subLabel.length > 0
        && this.subLabelWidth === 0) {
        ctx.font = "28px 'Poppins', sans-serif";
        this.subLabelWidth = ctx.measureText(this.subLabel).width;

      }

      // Main Label
      if (this.label
        && this.label.length > 0
        && this.labelWidth === 0) {
        ctx.font = "30px 'Poppins', sans-serif";
        this.labelWidth = ctx.measureText(this.label).width;

      }

      // Pricing
      if (this.price
        && this.price > 0
        && !this.priceWidth) {
        this.formattedPrice = this.price.toLocaleString('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        });
      }

      if (this.formattedPrice && this.priceWidth === 0) {
        this.priceWidth = ctx.measureText(this.formattedPrice).width + 20;
      }

      let labelOffset = 90;
      if (this.priceWidth > 0) {
        labelOffset = 120;
      }

      this.calculatedWidth = this.labelWidth + this.priceWidth + labelOffset;
      this.bounds.y = this.anchor.relativeY;
      this.bounds.width = this.calculatedWidth;
      this.bounds.height = 56;

      if (this.subLabel && this.subLabel.length > 0) {
        if (this.subLabelPosition === SubLabelPosition.top) {
          this.bounds.y = this.anchor.relativeY - 39;
          this.bounds.height += 39;
        } else {
          this.bounds.height += 39;
        }
      }
      this.isBoundsSet = true;
    }
  }

  /**
  * Draws the background container and main label for the tool tip
  */
  drawBackground(ctx: CanvasRenderingContext2D) {

    if (!this.isCollapsed) {
      if (this.isDesignerTip) {
        this.drawTipBackground(ctx);
      } else {
        this.drawUncollapsedBackground(ctx);
      }
    } else {
      if (this.isDesignerTip) {
        this.drawIconCircle(ctx);
      } else {
        this.drawCollapsedBackground(ctx);
      }
    }

    // Debug
    //ctx.strokeStyle = "#ff0000";
    //ctx.lineWidth = 1;
    //ctx.strokeRect(this.bounds.x, this.bounds.y, this.bounds.width, this.bounds.height);

  }

  drawIcon(ctx: CanvasRenderingContext2D) {
    // TODO: If selected, draw something. 
    //if (this.isCollapsed) return;

    let xOffset = 17;
    const yOffset = 17;

    if (this.iconPosition === IconPosition.right) {
      xOffset = this.bounds.width - xOffset - 24;
    }

    const icon = this.selected && !this.isDesignerMode ? this.selectedIcon : this.unselectedIcon;
    ctx.drawImage(icon, this.anchor.relativeX + xOffset, this.anchor.relativeY + yOffset, this.iconSize, this.iconSize);

    if (this.animatingClick) {
      this.animateClick();
    }

    // Debug
    //ctx.strokeStyle = "#ff0000";
    //ctx.lineWidth = 1;
    //ctx.strokeRect(this.anchor.x, this.anchor.y, 58, 58);
  }
  drawInfoIcon(ctx: CanvasRenderingContext2D) {
    // TODO: If selected, draw something. 
    //if (this.isCollapsed) return;

    let xOffset = 17;
    const yOffset = 17;

    if (this.iconPosition === IconPosition.right) {
      xOffset = this.bounds.width - xOffset - 24;
    }

    const icon = this.infoIcon;
    ctx.drawImage(icon, this.anchor.relativeX + xOffset, this.anchor.relativeY + yOffset, this.iconSize, this.iconSize);

    if (this.animatingClick) {
      this.animateClick();
    }

    // Debug
    //ctx.strokeStyle = "#ff0000";
    //ctx.lineWidth = 1;
    //ctx.strokeRect(this.anchor.x, this.anchor.y, 58, 58);
  }

  drawCollapsedBackground(ctx: CanvasRenderingContext2D) {

    //fadding out/in functionality stoped
    ctx.globalAlpha = 1;
    //ctx.globalAlpha = this.collapsedOpacity;
    //if (!this.isFading && this.collapsedOpacity <= .9) {
    //  this.collapsedOpacity += .005;
    //} else {
    //  this.isFading = true;
    //}

    //if (this.isFading && this.collapsedOpacity >= .2) {
    //  this.collapsedOpacity -= .005;
    //} else {
    //  this.isFading = false;
    //}
    let xOffset = 29;

    if (this.iconPosition === IconPosition.right) {
      xOffset = this.bounds.width - xOffset;
    }

    ctx.fillStyle = '#fff';
    //ctx.fillStyle = environment.theme.primaryColor;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(xOffset + this.anchor.relativeX, 28 + this.anchor.relativeY, 20, 0, 2 * Math.PI);
    ctx.strokeStyle = environment.theme.primaryColor;
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  }

  drawUncollapsedBackground(ctx: CanvasRenderingContext2D) {

    this.getBounds(ctx);

    let labelXOffset = 60;
    let iconXOffset = 29;

    if (this.iconPosition === IconPosition.right) {
      labelXOffset = 20;
      iconXOffset = this.bounds.width - iconXOffset;
    }

    ctx.font = "30px 'Poppins', sans-serif";
    ctx.fillStyle = this.isActive ? "#ffffffbb" : "#FA8072";


    DrawingLibrary.roundRect(ctx, this.anchor.relativeX, this.anchor.relativeY, this.calculatedWidth, 56, 33, true, this.isDesignerMode && this.isSelected);

    // Icon Circle

    //ctx.fillStyle = environment.theme.primaryColor;
    ctx.strokeStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.anchor.relativeX + iconXOffset, this.anchor.relativeY + 28, 20, 0, 2 * Math.PI);
    ctx.strokeStyle = environment.theme.primaryColor;
    ctx.fill();
    ctx.stroke();
    ctx.closePath();

    // Label
    ctx.fillStyle = "#000";
    ctx.fillText(this.label.toUpperCase(), labelXOffset + this.anchor.relativeX, 39 + this.anchor.relativeY);

    // Price
    if (this.price > 0) {
      ctx.font = "34px 'Poppins', sans-serif";
      ctx.fillText("+" + this.formattedPrice, labelXOffset + 20 + this.labelWidth + this.anchor.relativeX, 40 + this.anchor.relativeY);
    }

    // If a sub label exists, draw it.
    if (this.isDesignerMode || !this.inActive) {
      if (this.subLabel && this.subLabel.length > 0) {
        this.drawSubLabel(ctx);
      }
    }

    // Icon Debugging
    //let yOffset = 0;

    //if (this.subLabelPosition === SubLabelPosition.top) {
    //  yOffset = 39;
    //}

    //this.ctx.strokeStyle = "#ff0000";
    //this.ctx.lineWidth = 1;
    //this.ctx.strokeRect(this.bounds.x, this.bounds.y + yOffset, 58, 58);
  }

  drawIconCircle(ctx: CanvasRenderingContext2D) {
    // Icon Circle
    ctx.strokeStyle = "#fff";
    ctx.fillStyle = "#fff";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(this.anchor.relativeX + 29, this.anchor.relativeY + 28, 20, 0, 2 * Math.PI);
    ctx.strokeStyle = environment.theme.primaryColor;
    ctx.fill();
    ctx.stroke();
    ctx.closePath();
  }

  drawTipBackgroundWithoutImage(ctx: CanvasRenderingContext2D) {

    var centerPoints = { x: ctx.canvas.width / 2, y: ctx.canvas.height / 2 };

    let x = this.anchor.relativeX, y = this.anchor.relativeY;

    if (this.anchor.relativeX < centerPoints.x && this.anchor.relativeY < centerPoints.y) {
      this.tipPosition = TipPosition.topLeft;

      x = this.anchor.relativeX + 40
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x += 80; y += 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);

    }
    else if (this.anchor.relativeX > centerPoints.x && this.anchor.relativeY < centerPoints.y) {
      this.tipPosition = TipPosition.topRight;

      x = this.anchor.relativeX + 10
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x -= 80; y += 70
      ctx.lineTo(x, y);
      x -= 50;
      ctx.lineTo(x, y);
    }
    else if (this.anchor.relativeX < centerPoints.x && this.anchor.relativeY > centerPoints.y) {
      this.tipPosition = TipPosition.bottomLeft;

      x = this.anchor.relativeX + 40
      y = this.anchor.relativeY + 10;

      ctx.moveTo(x, y);
      x += 80; y -= 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);
    }
    else if (this.anchor.relativeX > centerPoints.x && this.anchor.relativeY > centerPoints.y) {
      this.tipPosition = TipPosition.bottomRight;

      x = this.anchor.relativeX + 20
      y = this.anchor.relativeY + 10;

      ctx.moveTo(x, y);
      x -= 80; y -= 70
      ctx.lineTo(x, y);
      x -= 50;
      ctx.lineTo(x, y);
    }
    else {
      this.tipPosition = TipPosition.topLeft;

      x = this.anchor.relativeX + 10
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x += 80; y += 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);
    }

    //box for text
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#fff";
    ctx.stroke();

    const minWidth = 400;
    const maxWidth = 750;
    let width = minWidth;
    ctx.font = 'bold italic 32px Brush Script MT, Brush Script Std, cursive';
    let headerWidth = ctx.measureText("Designer Tip - " + this.label).width;

    ctx.font = "28px 'Poppins', sans-serif";
    this.subLabelWidth = ctx.measureText(this.subLabel).width;

    if (headerWidth > minWidth) {
      width = headerWidth + 30;
    }
    if (headerWidth > maxWidth) {
      width = maxWidth;
    }
    if (this.subLabelWidth > headerWidth) {
      if (this.subLabelWidth > minWidth) {
        width = this.subLabelWidth + 30;
      }
      if (this.subLabelWidth > maxWidth) {
        width = maxWidth;
        let desc = this.subLabel.replace(/[\n\r]/g, '');
        let subLabelArray: string[] = [];
        if (desc.length > 50) {
          subLabelArray = this.splitToArray(desc, 50) //desc.match(/.{1,50}/g)
        } else {
          subLabelArray.push(desc);
        }
        ctx.font = "28px 'Poppins', sans-serif";
        for (let line of subLabelArray) {
          let lineWidth = ctx.measureText(line.trim()).width
          if (lineWidth >= width) {
            width = lineWidth + 30;
          }
        }
      }
    }

    let height = 60;
    if (this.label.length > 30) {
      let titleArray = this.splitToArray(this.label.substring(30), 42) //this.label.substring(30).match(/.{1,42}/g);
      for (let i = 0; i < titleArray.length; i++) {
        height += 35;
      }
    }

    this.subLabel = this.subLabel.replace(/[\n\r]/g, '');
    var subLabelArray = [];
    if (this.subLabel.length > 50) {
      subLabelArray = this.splitToArray(this.subLabel, 50)//.match(/.{1,50}/g)
    } else {
      subLabelArray.push(this.subLabel);
    }


    for (let i = 0; i < subLabelArray.length; i++) {
      height += 30;
    }
    if (this.label.length > 30) {
      height += 10;
    }
    y = y - (height / 2);

    ctx.fillStyle = '#ffffffbb';

    if (this.tipPosition === TipPosition.topLeft) {
      DrawingLibrary.roundRect(ctx, x, y, width, height, 20, true, true);
    } else if (this.tipPosition === TipPosition.topRight) {
      x -= width;
      DrawingLibrary.roundRect(ctx, x, y, width, height, 20, true, true);
    } else if (this.tipPosition === TipPosition.bottomLeft) {
      DrawingLibrary.roundRect(ctx, x, y, width, height, 20, true, true);
    } else if (this.tipPosition === TipPosition.bottomRight) {
      x -= width;
      DrawingLibrary.roundRect(ctx, x, y, width, height, 20, true, true);
    }

    // Label
    ctx.fillStyle = "#000";
    ctx.font = 'bold italic 32px Brush Script MT, Brush Script Std, cursive';
    ctx.fillText("Designer Tip -", x + 15, y + 35);

    let headerTextWidth = ctx.measureText("Designer Tip -").width;

    ctx.font = 'italic 32px Brush Script MT, Brush Script Std, cursive';
    if (this.label.length > 30) {
      let firstLine = this.label.substring(0, 30);
      ctx.fillText(" " + firstLine, x + headerTextWidth + 10, y + 35);
      let titleArray = this.splitToArray(this.label.substring(30), 42) //this.label.substring(30).match(/.{1,42}/g);
      y += 75;
      for (let i = 0; i < titleArray.length; i++) {
        ctx.fillText(titleArray[i].trim(), x + 15, y);
        y += 35;
      }
    } else {
      ctx.fillText(" " + this.label, x + headerTextWidth + 10, y + 35);
    }

    this.subLabel = this.subLabel.replace(/[\n\r]/g, '');
    var subLabelArray = [];
    if (this.subLabel.length > 50) {
      subLabelArray = this.splitToArray(this.subLabel, 50)//.match(/.{1,50}/g)
    } else {
      subLabelArray.push(this.subLabel);
    }

    if (this.label.length > 30) {
      y += 10;
    } else {
      y += 75;
    }

    ctx.font = "28px 'Poppins', sans-serif";
    for (let i = 0; i < subLabelArray.length; i++) {
      ctx.fillText(subLabelArray[i].trim(), x + 15, y);
      y += 30;
    }
  }

  drawTipBackgroundWithImage(ctx: CanvasRenderingContext2D) {
    var centerPoints = { x: ctx.canvas.width / 2, y: ctx.canvas.height / 2 };

    let x = this.anchor.relativeX, y = this.anchor.relativeY;

    if (this.anchor.relativeX < centerPoints.x && this.anchor.relativeY < centerPoints.y) {
      this.tipPosition = TipPosition.topLeft;

      x = this.anchor.relativeX + 40
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x += 80; y += 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);

    }
    else if (this.anchor.relativeX > centerPoints.x && this.anchor.relativeY < centerPoints.y) {
      this.tipPosition = TipPosition.topRight;

      x = this.anchor.relativeX + 10
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x -= 80; y += 70
      ctx.lineTo(x, y);
      x -= 50;
      ctx.lineTo(x, y);
    }
    else if (this.anchor.relativeX < centerPoints.x && this.anchor.relativeY > centerPoints.y) {
      this.tipPosition = TipPosition.bottomLeft;

      x = this.anchor.relativeX + 40
      y = this.anchor.relativeY + 10;

      ctx.moveTo(x, y);
      x += 80; y -= 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);
    }
    else if (this.anchor.relativeX > centerPoints.x && this.anchor.relativeY > centerPoints.y) {
      this.tipPosition = TipPosition.bottomRight;

      x = this.anchor.relativeX + 20
      y = this.anchor.relativeY + 10;

      ctx.moveTo(x, y);
      x -= 80; y -= 70
      ctx.lineTo(x, y);
      x -= 50;
      ctx.lineTo(x, y);
    }
    else {
      this.tipPosition = TipPosition.topLeft;

      x = this.anchor.relativeX + 10
      y = this.anchor.relativeY + 40;

      ctx.moveTo(x, y);
      x += 80; y += 70
      ctx.lineTo(x, y);
      x += 50;
      ctx.lineTo(x, y);
    }

    //box for text
    ctx.lineWidth = 4;
    ctx.strokeStyle = "#fff";
    ctx.stroke();

    let minWidth = 400;
    let maxWidth = 750;
    let width = minWidth;

    ctx.font = 'bold italic 32px Brush Script MT, Brush Script Std, cursive';
    let headerWidth = ctx.measureText("Designer Tip - " + this.label).width;

    ctx.font = "28px 'Poppins', sans-serif";
    this.subLabelWidth = ctx.measureText(this.subLabel).width;

    if (headerWidth > minWidth) {
      width = headerWidth + 30;
    }
    if (headerWidth > maxWidth) {
      width = maxWidth;
    }
    if (this.subLabelWidth > headerWidth) {
      if (this.subLabelWidth > minWidth) {
        width = this.subLabelWidth + 30;
      }
      if (this.subLabelWidth > maxWidth) {
        width = maxWidth;

        let desc = this.subLabel.replace(/[\n\r]/g, '');
        let subLabelArray: string[] = [];
        if (desc.length > 50) {
          subLabelArray = this.splitToArray(desc, 50) //desc.match(/.{1,50}/g)
        } else {
          subLabelArray.push(desc);
        }
        for (let line of subLabelArray) {
          let lineWidth = ctx.measureText(line.trim()).width + 30
          if (lineWidth >= width) {
            width = lineWidth;
          }
        }
      }
    }

    let height = 60;
    if (this.label?.length > 30) {
      let titleArray = this.splitToArray(this.label.substring(30), 42) //this.label.substring(30).match(/.{1,42}/g);
      for (let i = 0; i < titleArray.length; i++) {
        height += 35;
      }
    }

    this.subLabel = this.subLabel.replace(/[\n\r]/g, '');
    var subLabelArray = [];
    if (this.subLabel.length > 50) {
      subLabelArray = this.splitToArray(this.subLabel, 50) //this.subLabel.match(/.{1,50}/g)
    } else {
      subLabelArray.push(this.subLabel);
    }


    for (let i = 0; i < subLabelArray.length; i++) {
      height += 30;
    }
    if (this.label?.length > 30) {
      height += 10;
    }
    y = y - (height / 2);

    ctx.fillStyle = '#ffffffbb';
    this.SCImage = this.resourceManager.getImage("SC-Image");

    if (this.tipPosition === TipPosition.topLeft || this.tipPosition === TipPosition.bottomLeft) {

      let imageX = x;
      let imageY = y;

      x += (height / 2);
      DrawingLibrary.roundRect(ctx, x, y, width + (height / 2), height, 20, true, true);

      // Label
      ctx.fillStyle = "#000";
      ctx.font = 'bold italic 32px Brush Script MT, Brush Script Std, cursive';
      ctx.fillText("Designer Tip -", x + (height / 2) + 15, y + 35);

      let headerTextWidth = ctx.measureText("Designer Tip -").width;

      ctx.font = 'italic 32px Brush Script MT, Brush Script Std, cursive';
      if (this.label?.length > 30) {
        let firstLine = this.label.substring(0, 30);
        ctx.fillText(" " + firstLine, x + (height / 2) + headerTextWidth + 10, y + 35);
        let titleArray = this.splitToArray(this.label.substring(30), 42) //this.label.substring(30).match(/.{1,42}/g);
        y += 75;
        for (let i = 0; i < titleArray.length; i++) {
          ctx.fillText(titleArray[i].trim(), x + (height / 2) + 15, y);
          y += 35;
        }
      } else if (this.label) {
        ctx.fillText(" " + this.label, x + (height / 2) + headerTextWidth + 10, y + 35);
      }

      this.subLabel = this.subLabel.replace(/[\n\r]/g, '');
      var subLabelArray = [];
      if (this.subLabel.length > 50) {
        subLabelArray = this.splitToArray(this.subLabel, 50)//.match(/.{1,50}/g)
      } else {
        subLabelArray.push(this.subLabel);
      }

      if (this.label?.length > 30) {
        y += 10;
      } else {
        y += 75;
      }

      ctx.font = "28px 'Poppins', sans-serif";
      for (let i = 0; i < subLabelArray.length; i++) {
        ctx.fillText(subLabelArray[i].trim(), x + (height / 2) + 15, y);
        y += 30;
      }

      // Draw SC Image
      if (this.SCImage) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(imageX + (height / 2), imageY + (height / 2), (height / 2), 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(this.SCImage, imageX, imageY, height, height)
        ctx.beginPath();
        ctx.arc(imageX + (height / 2), imageY + (height / 2), (height / 2), 0, Math.PI * 2, true);
        ctx.clip();
        ctx.closePath();
      }
    }
    else if (this.tipPosition === TipPosition.topRight || this.tipPosition === TipPosition.bottomRight) {

      x -= (height / 2);
      let imageX = x;
      let imageY = y;

      //x -= (height / 2);
      width += + (height / 2);
      x -= width;

      DrawingLibrary.roundRect(ctx, x, y, width, height, 20, true, true);

      // Label
      ctx.fillStyle = "#000";
      ctx.font = 'bold italic 32px Brush Script MT, Brush Script Std, cursive';
      ctx.fillText("Designer Tip -", x + 15, y + 35);

      let headerTextWidth = ctx.measureText("Designer Tip -").width;

      ctx.font = 'italic 32px Brush Script MT, Brush Script Std, cursive';
      if (this.label.length > 30) {
        let firstLine = this.label.substring(0, 30);
        ctx.fillText(" " + firstLine, x + headerTextWidth + 10, y + 35);
        let titleArray = this.splitToArray(this.label.substring(30), 42) //this.label.substring(30).match(/.{1,42}/g);
        y += 75;
        for (let i = 0; i < titleArray.length; i++) {
          ctx.fillText(titleArray[i].trim(), x + 15, y);
          y += 35;
        }
      } else {
        ctx.fillText(" " + this.label, x + headerTextWidth + 10, y + 35);
      }

      this.subLabel = this.subLabel.replace(/[\n\r]/g, '');
      var subLabelArray = [];
      if (this.subLabel.length > 50) {
        subLabelArray = this.splitToArray(this.subLabel, 50)//.match(/.{1,50}/g)
      } else {
        subLabelArray.push(this.subLabel);
      }

      if (this.label.length > 30) {
        y += 10;
      } else {
        y += 75;
      }

      ctx.font = "28px 'Poppins', sans-serif";
      for (let i = 0; i < subLabelArray.length; i++) {
        ctx.fillText(subLabelArray[i].trim(), x + 15, y);
        y += 30;
      }

      //draw SC image
      if (this.SCImage) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(imageX, imageY + (height / 2), (height / 2), 0, Math.PI * 2, true);
        ctx.closePath();
        ctx.clip();
        ctx.drawImage(this.SCImage, imageX - (height / 2), imageY, height, height)
        ctx.beginPath();
        ctx.arc(imageX, imageY + (height / 2), (height / 2), 0, Math.PI * 2, true);
        ctx.clip();
        ctx.closePath();
      }
    }
    ctx.restore()
  }

  splitToArray(text: string, lineSize = 50): string[] {
    const lineArray: string[] = [];
    let charArray = text.split('')
    let lineCount = 1;
    let line = '';
    for (let i = 0; i < charArray.length; i++) {
      if (i < (lineSize + lineArray.join(' ').length)) {
        line += charArray[i];
        if (i === (charArray.length - 1)) {
          lineArray.push(line)
        }
      } else if (charArray[i] === ' ') {
        if (lineCount > 1 && line.length > lineArray[0].length) {
          let array = line.split(' ')
          let extraWord = array.pop();
          lineArray.push(array.join(' '))
          line = extraWord + ' ';
        } else {
          lineArray.push(line)
          line = '';
        }
        lineCount++;
      }
      else {
        line += charArray[i];
      }
    }
    return lineArray
  }

  drawTipBackground(ctx: CanvasRenderingContext2D) {
    this.drawIconCircle(ctx);
    if (this.resourceManager.getImage("SC-Image")) {
      this.drawTipBackgroundWithImage(ctx)
    } else {
      this.drawTipBackgroundWithoutImage(ctx)
    }
  }

  getScaledCoordinates(x: number, y: number) {
    return { x: x * this.anchor.scale, y: y * this.anchor.scale };
  }

  drawSubLabel(ctx: CanvasRenderingContext2D) {

    ctx.fillStyle = "#fedd59ff";
    ctx.textAlign = "start";
    ctx.font = "28px 'Poppins', sans-serif";

    this.subLabelWidth = ctx.measureText(this.subLabel.toUpperCase()).width;

    const subLabelWidth = this.subLabelWidth + 20;

    const startX = this.anchor.relativeX + 60;
    let startY = this.anchor.relativeY + 60;

    const startXLine = startX - 30;
    let startYLine = startY;

    const endXLine = startX + 2;
    let endYLine = startY + 33;

    if (this.subLabelPosition === SubLabelPosition.top) {
      startY = this.anchor.relativeY - 39;
      startYLine = startYLine - 63;
      endYLine = startY + 2;
    }
    // Draw Background

    ctx.fillStyle = this.inActive ? "#FA8072" : environment.theme.secondaryColor;
    ctx.fillRect(startX, startY, subLabelWidth, 35);

    // Draw line on background
    ctx.fillStyle = environment.theme.primaryColor;
    ctx.fillRect(startX + subLabelWidth, startY, 10, 35);

    // Draw Text
    ctx.fillStyle = "#000";
    ctx.fillText(this.subLabel.toUpperCase(), startX + 10, startY + 28);

    // Draw Line
    ctx.strokeStyle = environment.theme.secondaryColor;
    ctx.lineWidth = 6;

    ctx.beginPath();
    ctx.moveTo(startXLine, startYLine);
    ctx.lineTo(endXLine, endYLine);
    ctx.stroke();
  }

  isIconClicked(x: number, y: number): boolean {

    const yOffset = 0;
    let xOffset = 0;
    const iconAreaWidth = 58;

    if (this.iconPosition === IconPosition.right) {
      xOffset = this.bounds.width - iconAreaWidth;
    }

    if (x >= this.anchor.relativeX + xOffset
      && y >= this.anchor.relativeY + yOffset
      && x <= this.anchor.relativeX + xOffset + iconAreaWidth
      && y <= this.anchor.relativeY + yOffset + iconAreaWidth) {
      this.initClickAnimation();
      return true;
    }
    return false;
  }

  isIconHover(ctx: CanvasRenderingContext2D, x: number, y: number): boolean {
    const yOffset = 0;
    let xOffset = 0;
    const iconAreaWidth = 58;

    if (this.iconPosition === IconPosition.right && this.bounds) {
      xOffset = this.bounds.width - iconAreaWidth;
    }

    if (this.anchor && x >= this.anchor.relativeX + xOffset
      && y >= this.anchor.relativeY + yOffset
      && x <= this.anchor.relativeX + xOffset + iconAreaWidth
      && y <= this.anchor.relativeY + yOffset + iconAreaWidth) {
      if (this.isCollapsed) {
        if (this.isDesignerTip && this.isDesignerMode) {
          this.drawTipBackground(ctx)
        } else if (!this.isDesignerTip) {
          this.drawUncollapsedBackground(ctx)
        }
      }
      return true;
    }
    return false;
  }

  initClickAnimation() {
    this.animatingClick = true;
    this.animationIconSize = 25;
    this.animationOpacity = 1;
  }

  animateClick() {

    this.animatingClick = false;
    //const offset = 0;//his.animationIconSize - this.iconSize;

    //const globalAlpha = this.ctx.globalAlpha;
    //this.ctx.globalAlpha = this.animationOpacity;

    //this.animationOpacity -= .04;
    //this.animationIconSize += 2;

    //if (this.animationIconSize >= 50) {
    //  this.animatingClick = false;
    //  this.animationOpacity = 0;
    //}

    //this.ctx.drawImage(this.selectedIcon, this.anchor.x + 17 - offset, this.anchor.y + 17 - offset, this.animationIconSize, this.animationIconSize);


    //this.ctx.globalAlpha = globalAlpha;
  }
}
